/**
 * Public Props interface for <ScatterPlot3D>. Extracted so the orchestrator
 * file stays focused on rendering. Importers should re-export from
 * 'components/ScatterPlot3D' if they want both Props and the component.
 */
import type { ScoredStock } from '@/lib/compute';
import type { ProjectionHorizon } from '@/lib/projectScore';

export interface TechnicalStock {
  symbol: string;
  technicalScore: number;
}

export type TierFilterKey = 'all' | 'alpha' | 'top10q' | 'top20q' | 'top15q' | 'combined';

export interface ScatterPlot3DProps {
  stocks: ScoredStock[];
  totalCount?: number;
  technicalData: TechnicalStock[];
  technicalWeeklyData: TechnicalStock[];
  technicalMonthlyData: TechnicalStock[];
  highlightSymbol: string;
  onSelectStock: (symbol: string) => void;
  selectedSymbol: string | null;
  enteredAlphaSymbols?: Set<string>;
  exitedAlphaSymbols?: Set<string>;
  historicalPeriod?: string | null;
  historicalDate?: string | null;
  historicalLoading?: boolean;
  onHistoricalPeriodChange?: (period: string | null) => void;
  updatedAt?: string;
  portfolioIsolated?: boolean;
  projectionHorizon?: ProjectionHorizon;
  onProjectionChange?: (horizon: ProjectionHorizon) => void;
  factorHighlightStocks?: Set<string>;
  sectorTopComposite?: Set<string>;
  alphaZoneStocks?: Set<string>;
  /** Hide the earnings calendar overlay. Used by the alpha-zone basket-only
   *  embed where today's earnings list is irrelevant noise. Default: false. */
  hideEarningsCalendar?: boolean;
  /** Position of the in-scene legend overlay. Defaults to 'top-left' (home);
   *  alpha-zone basket embed uses 'bottom-right' to clear the bottom-left
   *  alerts overlay. */
  legendPosition?: 'top-left' | 'bottom-right';
  /** Override the tier-filter button set rendered by ScatterControlsOverlay.
   *  Default (universe context) is a single Top 15 Q pill. Portfolio
   *  Visualizer overrides with `[{ key: 'combined', label: 'Combined' }]`. */
  tierButtons?: ReadonlyArray<{ key: TierFilterKey; label: string; title?: string }>;
  /** Notify the parent when the tier filter changes — used by the Portfolio
   *  Visualizer to swap to a single super-planet when 'combined' is active. */
  onTierFilterChange?: (next: TierFilterKey) => void;
  /** V-axis mode toggle: Classic V (peer-z only) vs Blended V (production:
   *  0.75·peer-z + 0.25·universe-z absolute). When provided, the scatter
   *  overlay shows a Classic/Blended pill alongside Top 15 Q. The caller is
   *  responsible for swapping `valuationScore` on the stocks based on the
   *  chosen mode (this component just renders the toggle). */
  vMode?: 'classic' | 'blended';
  onVModeChange?: (mode: 'classic' | 'blended') => void;
}

/**
 * Strip trailing corporate suffixes ("Inc.", "Corp.", etc.) from a company name
 * and return a short readable form for the compact portfolio popup table.
 * If the first word is ≥4 chars we use just that; if it's shorter (e.g. "Lam"),
 * we include the second word so the result is recognizable.
 *   "Micron Technology, Inc."        → "Micron"
 *   "Lam Research Corporation"        → "Lam Research"
 *   "Apple Inc."                      → "Apple"
 */
export function shortName(name: string | undefined | null): string {
  if (!name) return '';
  const cleaned = name
    .replace(
      /[,.]?\s+(Inc\.?|Incorporated|Corp\.?|Corporation|Co\.?|Company|Ltd\.?|Limited|Plc\.?|S\.A\.?|AG|NV|LLC|Holdings?|Group|Class\s+[AB])\.?$/i,
      '',
    )
    .trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length === 0) return name;
  if (parts[0].length >= 4 || parts.length === 1) return parts[0];
  return parts.slice(0, 2).join(' ');
}
